/**
 * The frame every dashboard card shares: a `Surface` with the 32px uppercase
 * header (icon, title, mono count, a "View all" chevron) and the loading /
 * empty / error states, so the cards only describe their rows. Cards sit side
 * by side in a `CardGrid`.
 */
import { AlertCircle, ChevronRight, type LucideIcon } from "lucide-react";
import type { FC, ReactNode } from "react";

import { Surface, SurfaceHeader } from "@/components/surface";
import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle
} from "@/components/ui/empty";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

export type DashboardCardProps = {
  icon: LucideIcon;
  title: string;
  /** Shown in the header badge when > 0 (or when `showZero`). */
  count?: number | null;
  showZero?: boolean;
  /** Header controls (a Mine/Team toggle, a filter). */
  controls?: ReactNode;
  onViewAll?: () => void;
  viewAllLabel?: string;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  /** Rows to render; when empty the `empty` state shows. */
  isEmpty: boolean;
  emptyTitle: string;
  emptyDescription?: string;
  emptyIcon?: LucideIcon;
  /** Rendered under the rows (a "Show n more" toggle). */
  footer?: ReactNode;
  /** Span the full row of a two-column `CardGrid`. */
  span?: "full";
  children?: ReactNode;
  className?: string;
  testId?: string;
};

export const DashboardCard: FC<DashboardCardProps> = ({
  icon: Icon,
  title,
  count,
  showZero = false,
  controls,
  onViewAll,
  viewAllLabel = "View All",
  isLoading = false,
  error,
  onRetry,
  isEmpty,
  emptyTitle,
  emptyDescription,
  emptyIcon: EmptyIcon,
  footer,
  span,
  children,
  className,
  testId
}) => {
  const showCount =
    typeof count === "number" && (count > 0 || showZero) && !isLoading;
  return (
    <Surface className={className} data-testid={testId} data-span={span}>
      <SurfaceHeader
        icon={Icon}
        title={title}
        count={showCount ? count : null}
        actions={
          controls || onViewAll ? (
            <>
              {controls}
              {onViewAll ? (
                <Button
                  variant="ghost"
                  size="xs"
                  className="gap-0.5 px-1.5 text-xs text-muted-foreground hover:text-foreground"
                  onClick={onViewAll}
                  data-testid="card-view-all"
                >
                  {viewAllLabel}
                  <ChevronRight className="size-3" />
                </Button>
              ) : null}
            </>
          ) : null
        }
      />
      {isLoading ? (
        <div className="space-y-2.5 px-3 py-3" data-testid="card-loading">
          <Skeleton className="h-3.5 w-3/4 rounded-sm" />
          <Skeleton className="h-3.5 w-1/2 rounded-sm" />
          <Skeleton className="h-3.5 w-2/3 rounded-sm" />
        </div>
      ) : error ? (
        <div
          className="flex items-center gap-2 px-3 py-2.5 text-xs text-destructive"
          data-testid="card-error"
        >
          <AlertCircle className="size-3.5 shrink-0" />
          <span className="min-w-0 flex-1 truncate">{error}</span>
          {onRetry ? (
            <Button
              variant="ghost"
              size="xs"
              className="text-xs"
              onClick={onRetry}
            >
              Retry
            </Button>
          ) : null}
        </div>
      ) : isEmpty ? (
        <Empty className="border-0 py-5" data-testid="card-empty">
          <EmptyHeader>
            <EmptyMedia
              variant="icon"
              className="size-7 rounded-sm text-muted-foreground [&_svg:not([class*='size-'])]:size-3.5"
            >
              {EmptyIcon ? <EmptyIcon /> : <Icon />}
            </EmptyMedia>
            <EmptyTitle className="text-base">{emptyTitle}</EmptyTitle>
            {emptyDescription ? (
              <EmptyDescription className="text-xs">
                {emptyDescription}
              </EmptyDescription>
            ) : null}
          </EmptyHeader>
        </Empty>
      ) : (
        <>
          <ul className="divide-y divide-border">{children}</ul>
          {footer}
        </>
      )}
    </Surface>
  );
};

/** One row of a card: content on the left, meta on the right, chevron. */
export const CardRow: FC<{
  onClick?: () => void;
  leading?: ReactNode;
  primary: ReactNode;
  secondary?: ReactNode;
  trailing?: ReactNode;
  className?: string;
  testId?: string;
}> = ({
  onClick,
  leading,
  primary,
  secondary,
  trailing,
  className,
  testId
}) => {
  const body = (
    <>
      {leading ? <span className="shrink-0">{leading}</span> : null}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-base leading-5">{primary}</span>
        {secondary ? (
          <span className="block truncate text-xs leading-4 text-muted-foreground">
            {secondary}
          </span>
        ) : null}
      </span>
      {trailing ? (
        <span className="flex shrink-0 items-center gap-2">{trailing}</span>
      ) : null}
      {onClick ? <ChevronRight className="size-3 shrink-0 text-fg-3" /> : null}
    </>
  );
  return (
    <li data-testid={testId}>
      {onClick ? (
        // A div, not a button: rows hold entity links (anchors) of their own.
        <div
          role="button"
          tabIndex={0}
          onClick={onClick}
          onKeyDown={(e) => {
            if (e.target !== e.currentTarget) return;
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onClick();
            }
          }}
          className={cn(
            "flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-fill-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
            className
          )}
        >
          {body}
        </div>
      ) : (
        <div className={cn("flex items-center gap-3 px-3 py-2", className)}>
          {body}
        </div>
      )}
    </li>
  );
};

/** "Show n more" footer toggle used by cards that collapse secondary rows. */
export const ShowMoreFooter: FC<{
  hidden: number;
  expanded: boolean;
  onToggle: () => void;
  label?: string;
}> = ({ hidden, expanded, onToggle, label = "More" }) => {
  if (!hidden && !expanded) return null;
  return (
    <div className="border-t border-border px-2 py-1">
      <Button
        variant="ghost"
        size="xs"
        className="text-xs text-muted-foreground"
        onClick={onToggle}
        data-testid="card-show-more"
      >
        {expanded ? "Show Less" : `Show ${hidden} ${label}`}
      </Button>
    </div>
  );
};
